// Family dashboard — 1:1 port of legacy
// `src/components/account/family/family-dashboard.tsx` adapted for the
// TanStack Router SPA shell. Differences vs legacy:
//   - URL state moves from `useSearchParams` to TanStack `validateSearch` so
//     deep links (`?member=…&tab=…`) survive route bundling.
//   - i18n via `useLingui` + `<Trans>` (legacy used next-intl).
//   - Empty state is gone — there's always a "self" profile, so legacy's
//     marketing card never showed in practice. Its benefits + plan info
//     have been promoted to a permanent "Plan" tab next to the family
//     content (Tudor's call). That tab also surfaces the live
//     `user.family_members.max` gate state from the catalog.
//   - Pending invites strip is folded into every tab (matches legacy
//     bottom-of-content pattern).

import { useCallback, useMemo, useRef, useState } from "react";

import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { providerFullDisplayName } from "@pontimed/types";
import { ApiError, apiFetch } from "@pontimed/api-client";
import { cn } from "@pontimed/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { AnimatePresence, m } from "framer-motion";
import {
  Baby,
  Calendar,
  CheckCircle2,
  Clock,
  Crown,
  Heart,
  LayoutGrid,
  Mail,
  Phone,
  Pill,
  Plus,
  Ruler,
  Shield,
  Sparkles,
  Syringe,
  Trash2,
  UserPlus,
  Users,
  X,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { AccountSecurityBanner } from "@/components/account/account-security-banner";
import { AddMemberSheet } from "@/components/account/family/add-member-sheet";
import { PageHeader } from "@/components/layout/page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ResponsiveSheet } from "@/components/ui/responsive-sheet";
import { Skeleton } from "@/components/ui/skeleton";
import {
  SwipeableTabsContent,
  type SwipeableTabsHandle,
} from "@/components/ui/swipeable-tabs-content";
import { TabBar, type TabItem } from "@/components/ui/tab-bar";
import { resolveFeature } from "@/lib/features/access";
import { showToast } from "@/lib/ui/toast";

// ─── Types ────────────────────────────────────────────────────────────────

type Relationship = "child" | "parent" | "spouse" | "other";

interface MemberAppointment {
  id: string;
  startsAt: string;
  type: string;
  status: string;
  provider: { firstName: string; lastName: string; profession?: string | null };
}

interface FamilyMember {
  id: string;
  firstName: string;
  lastName: string;
  dateOfBirth: string | null;
  gender: string | null;
  photoUrl: string | null;
  familyRole: Relationship | "self" | string;
  guardianProfileId?: string | null;
  appointments?: MemberAppointment[];
  _count?: {
    prescriptions: number;
    vaccinationRecords: number;
    growthRecords: number;
  };
}

interface PendingInvite {
  id: string;
  targetEmail: string | null;
  targetPhone: string | null;
  role: Relationship;
  createdAt: string;
}

interface FamilyGroup {
  id: string;
  name: string | null;
  ownerProfileId: string;
  maxMembers: number;
  isOwner: boolean;
}

interface FamilyResponse {
  members: FamilyMember[];
  group: FamilyGroup | null;
  pendingInvites?: PendingInvite[];
}

interface BillingSubscription {
  id: string;
  entityType: string;
  entityId: string;
  planTier: string | null;
  status: string;
  plan: { code: string; name: unknown; features: unknown } | null;
}

interface BillingResponse {
  subscriptions: BillingSubscription[];
}

// ─── Tabs ─────────────────────────────────────────────────────────────────

type Tab =
  | "overview"
  | "appointments"
  | "medications"
  | "growth"
  | "vaccinations"
  | "pregnancy"
  | "prevention"
  | "plan";

const ALL_TAB_IDS: readonly Tab[] = [
  "overview",
  "appointments",
  "medications",
  "growth",
  "vaccinations",
  "pregnancy",
  "prevention",
  "plan",
];

// Member pill colour rotation — same hues as legacy.
const MEMBER_COLORS = [
  "bg-teal-500",
  "bg-indigo-500",
  "bg-amber-500",
  "bg-rose-500",
  "bg-emerald-500",
  "bg-violet-500",
];

const FAMILY_KEY = ["api", "/api/account/family"] as const;
const BILLING_KEY = ["api", "/api/account/billing"] as const;

const familyQueryOptions = {
  queryKey: FAMILY_KEY,
  queryFn: () => apiFetch.get<FamilyResponse>("/api/account/family"),
} as const;

const billingQueryOptions = {
  queryKey: BILLING_KEY,
  queryFn: async (): Promise<BillingResponse | null> => {
    try {
      return await apiFetch.get<BillingResponse>("/api/account/billing");
    } catch (err) {
      if (err instanceof ApiError && (err.status === 401 || err.status === 404)) return null;
      throw err;
    }
  },
  staleTime: 60_000,
} as const;

// ─── Route ────────────────────────────────────────────────────────────────

interface FamilySearch {
  member?: string;
  tab: Tab;
}

export const Route = createFileRoute("/_authenticated/account/user/family")({
  validateSearch: (search: Record<string, unknown>): FamilySearch => {
    const rawTab = typeof search.tab === "string" ? search.tab : "";
    const rawMember = typeof search.member === "string" ? search.member : undefined;
    return {
      member: rawMember,
      tab: (ALL_TAB_IDS as readonly string[]).includes(rawTab) ? (rawTab as Tab) : "overview",
    };
  },
  // Prefetch family + billing in parallel — the page can't render meaningful
  // content without family (members list) and the badge in the header needs
  // billing. Both fire concurrently while the route chunk downloads.
  loader: ({ context }) => {
    void context.queryClient.prefetchQuery(billingQueryOptions);
    return context.queryClient.ensureQueryData(familyQueryOptions);
  },
  component: FamilyPage,
});

// ─── Helpers ──────────────────────────────────────────────────────────────

function getAge(dateOfBirth: string | null, t: ReturnType<typeof useLingui>["t"]): string | null {
  if (!dateOfBirth) return null;
  const birth = new Date(dateOfBirth);
  const now = new Date();
  const years = now.getFullYear() - birth.getFullYear();
  const months = now.getMonth() - birth.getMonth();
  if (years < 2) {
    const totalMonths = years * 12 + months;
    return t(msg`${totalMonths} mo`);
  }
  return t(msg`${years} yrs`);
}

function getInitials(first: string, last: string): string {
  return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
}

function roleLabel(role: string, t: ReturnType<typeof useLingui>["t"]): string {
  if (role === "self") return t(msg`You`);
  if (role === "child") return t(msg`Child`);
  if (role === "parent") return t(msg`Parent`);
  if (role === "spouse") return t(msg`Spouse`);
  return t(msg`Member`);
}

// Resolve user-scope plan code from billing — falls back to free plan.
function pickUserPlanCode(billing: BillingResponse | null | undefined): string {
  if (!billing) return "user_free";
  const subs = billing.subscriptions ?? [];
  const userSub = subs.find(
    (s) => s.status === "active" && (s.entityType === "user" || s.entityType === "patient"),
  );
  if (userSub?.plan?.code) return userSub.plan.code;
  // Auto-promote: any active paid sub on this account → user_plus.
  if (subs.some((s) => s.status === "active")) return "user_plus";
  return "user_free";
}

// Humanized fallback when the billing payload has no localized name for the
// plan — "user_plus" → "Plus", "user_family" → "Family". Never render the
// raw plan code.
function humanizePlanCode(code: string): string {
  return code
    .replace(/^(user|patient)_/, "")
    .split("_")
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : w))
    .join(" ");
}

// Localized plan display name — same fallback chain as the billing screens'
// `planDisplayName` (unified-billing-view): locale → en → ro → humanized code.
function pickUserPlanName(
  billing: BillingResponse | null | undefined,
  planCode: string,
  locale: string,
): string {
  const sub = (billing?.subscriptions ?? []).find(
    (s) => s.status === "active" && s.plan?.code === planCode,
  );
  const name = sub?.plan?.name as Record<string, string> | null | undefined;
  return name?.[locale] || name?.en || name?.ro || humanizePlanCode(planCode);
}

// ─── Page ─────────────────────────────────────────────────────────────────

function FamilyPage() {
  const { t, i18n } = useLingui();
  const navigate = useNavigate();
  const { member: memberParam, tab } = Route.useSearch();
  const swipeRef = useRef<SwipeableTabsHandle>(null);
  const [showAddSheet, setShowAddSheet] = useState(false);
  const [showInviteSheet, setShowInviteSheet] = useState(false);

  const familyQuery = useQuery(familyQueryOptions);
  const billingQuery = useQuery(billingQueryOptions);

  const data: FamilyResponse = familyQuery.data ?? {
    members: [],
    group: null,
    pendingInvites: [],
  };

  const planCode = pickUserPlanCode(billingQuery.data);
  const planName = pickUserPlanName(billingQuery.data, planCode, i18n.locale);
  const memberCount = data.members.length;
  const featureAccess = resolveFeature(planCode, "user.family_members.max", memberCount);
  const maxMembers =
    typeof featureAccess.limit === "number" && featureAccess.limit !== -1
      ? featureAccess.limit
      : (data.group?.maxMembers ?? null);
  const limitReached =
    typeof maxMembers === "number" && maxMembers > 0 && memberCount >= maxMembers;

  const isOwner = data.group?.isOwner ?? !data.group;

  // Selected member resolution — URL → first member.
  const selectedId = useMemo(() => {
    if (memberParam && data.members.some((m) => m.id === memberParam)) return memberParam;
    return data.members[0]?.id ?? null;
  }, [memberParam, data.members]);
  const selected = data.members.find((m) => m.id === selectedId) ?? null;

  const isChild = selected?.familyRole === "child";
  const isAdultFemale = selected?.gender === "F" && !isChild;

  // Build visible tabs — Plan tab is always last, other rules match legacy.
  const allTabs: { id: Tab; label: string; icon: ReactIcon; show: boolean }[] = [
    { id: "overview", label: t`Overview`, icon: Heart, show: true },
    { id: "appointments", label: t`Appointments`, icon: Calendar, show: true },
    { id: "medications", label: t`Medications`, icon: Pill, show: true },
    { id: "growth", label: t`Growth`, icon: Ruler, show: !!isChild },
    { id: "vaccinations", label: t`Vaccinations`, icon: Syringe, show: true },
    { id: "pregnancy", label: t`Pregnancy`, icon: Baby, show: !!isAdultFemale },
    { id: "prevention", label: t`Prevention`, icon: Shield, show: true },
    { id: "plan", label: t`Plan`, icon: Sparkles, show: true },
  ];
  const visibleTabs = allTabs.filter((x) => x.show);
  const visibleTabIds = visibleTabs.map((x) => x.id);
  // Re-validate URL tab against currently visible — fall back to overview
  // when the deep-link tab is hidden for the active member (e.g. ?tab=growth
  // on an adult).
  const activeTab: Tab = (visibleTabIds as string[]).includes(tab) ? tab : "overview";

  const setTab = useCallback(
    (next: Tab) =>
      navigate({
        to: "/account/user/family",
        search: (prev) => ({ ...prev, tab: next }),
      }),
    [navigate],
  );

  const setMember = useCallback(
    (id: string) =>
      // Reset to overview when switching members so we never land on a hidden
      // tab (growth on adult, pregnancy on male).
      navigate({
        to: "/account/user/family",
        search: { member: id, tab: "overview" },
      }),
    [navigate],
  );

  if (familyQuery.isLoading) return <FamilySkeleton />;
  if (familyQuery.isError) return <ErrorState onRetry={() => familyQuery.refetch()} />;

  const tabItems: TabItem<Tab>[] = visibleTabs.map((x) => {
    const Icon = x.icon;
    return { id: x.id, label: x.label, icon: <Icon className="size-4" /> };
  });

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Header */}
      <PageHeader
        title={<Trans>Family</Trans>}
        description={<Trans>Manage health for your whole family.</Trans>}
        actions={
          isOwner ? (
            <Button size="touch" variant="outline" onClick={() => setShowInviteSheet(true)}>
              <Mail className="size-4" aria-hidden />
              <Trans>Invite</Trans>
            </Button>
          ) : undefined
        }
      />

      {/* Member pills */}
      <div className="mt-4 flex flex-wrap gap-2">
        {data.members.map((m, i) => {
          const isSelected = m.id === selectedId;
          const color = MEMBER_COLORS[i % MEMBER_COLORS.length];
          const memberAge = getAge(m.dateOfBirth, t);
          return (
            <button
              key={m.id}
              type="button"
              onClick={() => setMember(m.id)}
              className={cn(
                "flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium whitespace-nowrap transition-all",
                "min-h-[44px]",
                isSelected
                  ? "border-primary bg-primary/10 text-primary"
                  : "border-border bg-card text-muted-foreground hover:border-primary/40 hover:text-foreground",
              )}
              aria-pressed={isSelected}
            >
              {m.photoUrl ? (
                <img
                  src={m.photoUrl}
                  alt=""
                  className="size-6 shrink-0 rounded-full object-cover"
                />
              ) : (
                <div
                  className={cn(
                    "flex size-6 shrink-0 items-center justify-center rounded-full text-[10px] font-bold text-white",
                    color,
                  )}
                >
                  {getInitials(m.firstName, m.lastName)}
                </div>
              )}
              <span>{m.firstName}</span>
              {memberAge && <span className="text-muted-foreground text-xs">({memberAge})</span>}
            </button>
          );
        })}

        {/* Add member pill */}
        {isOwner && (
          <button
            type="button"
            onClick={() => setShowAddSheet(true)}
            className="border-border text-muted-foreground hover:border-primary/40 hover:text-foreground flex min-h-[44px] items-center gap-1.5 rounded-full border border-dashed px-4 py-2 text-sm font-medium whitespace-nowrap transition-all"
          >
            <Plus className="size-4" />
            <Trans>Add member</Trans>
          </button>
        )}
      </div>

      {/* Selected member content */}
      <AnimatePresence mode="wait">
        <m.div
          key={selected?.id ?? "plan-only"}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.2 }}
          className="flex min-h-0 flex-1 flex-col"
        >
          <TabBar
            tabs={tabItems}
            activeTab={activeTab}
            onTabChange={setTab}
            onActiveTabClick={() => swipeRef.current?.scrollActiveToTop()}
            className="mt-6"
          />

          <SwipeableTabsContent
            ref={swipeRef}
            activeTab={activeTab}
            tabIds={visibleTabIds as readonly Tab[]}
            onTabChange={setTab}
            className="mt-4"
            renderPanel={(id) => (
              <PanelWrapper pendingInvites={data.pendingInvites ?? []}>
                {id === "plan" ? (
                  <PlanTab
                    planCode={planCode}
                    planName={planName}
                    memberCount={memberCount}
                    maxMembers={maxMembers}
                    limitReached={limitReached}
                    onAddMember={() => setShowAddSheet(true)}
                    onInvite={() => setShowInviteSheet(true)}
                    isOwner={isOwner}
                  />
                ) : selected ? (
                  <MemberPanel
                    tab={id as Exclude<Tab, "plan">}
                    member={selected}
                    canRemove={isOwner && selected.id !== data.group?.ownerProfileId}
                  />
                ) : null}
              </PanelWrapper>
            )}
          />
        </m.div>
      </AnimatePresence>

      <AddMemberSheet
        open={showAddSheet}
        onClose={() => setShowAddSheet(false)}
        onAdded={() => {
          setShowAddSheet(false);
          familyQuery.refetch();
        }}
        planCode={planCode}
        currentMemberCount={memberCount}
      />

      <InviteSheet
        open={showInviteSheet}
        onClose={() => setShowInviteSheet(false)}
        onInvited={() => {
          setShowInviteSheet(false);
          familyQuery.refetch();
        }}
      />
    </div>
  );
}

type ReactIcon = LucideIcon;

// ─── Panel wrapper — pending invites + tab body ───────────────────────────

function PanelWrapper({
  children,
  pendingInvites,
}: {
  children: React.ReactNode;
  pendingInvites: PendingInvite[];
}) {
  return (
    <div className="space-y-6 pb-6">
      {children}
      {pendingInvites.length > 0 && <PendingInvitesPanel invites={pendingInvites} />}
      <AccountSecurityBanner />
    </div>
  );
}

// ─── Member panel router ──────────────────────────────────────────────────

function MemberPanel({
  tab,
  member,
  canRemove,
}: {
  tab: Exclude<Tab, "plan">;
  member: FamilyMember;
  canRemove: boolean;
}) {
  if (tab === "overview") return <OverviewTab member={member} canRemove={canRemove} />;
  if (tab === "appointments")
    return (
      <PlaceholderTab
        icon={<Calendar className="size-8" />}
        title={<Trans>Appointments</Trans>}
        desc={<Trans>Upcoming visits and consultation history.</Trans>}
      />
    );
  if (tab === "medications")
    return (
      <PlaceholderTab
        icon={<Pill className="size-8" />}
        title={<Trans>Medications</Trans>}
        desc={<Trans>Active prescriptions, daily schedule, refill alerts.</Trans>}
      />
    );
  if (tab === "growth")
    return (
      <PlaceholderTab
        icon={<Ruler className="size-8" />}
        title={<Trans>Growth</Trans>}
        desc={<Trans>WHO growth curves, height, weight, percentiles.</Trans>}
      />
    );
  if (tab === "vaccinations")
    return (
      <PlaceholderTab
        icon={<Syringe className="size-8" />}
        title={<Trans>Vaccinations</Trans>}
        desc={<Trans>Vaccine calendar, completed and scheduled doses.</Trans>}
      />
    );
  if (tab === "pregnancy")
    return (
      <PlaceholderTab
        icon={<Baby className="size-8" />}
        title={<Trans>Pregnancy</Trans>}
        desc={<Trans>Weekly tracker, symptoms, movements, prenatal labs.</Trans>}
      />
    );
  if (tab === "prevention")
    return (
      <PlaceholderTab
        icon={<Shield className="size-8" />}
        title={<Trans>Prevention</Trans>}
        desc={<Trans>Age- and gender-based preventive screenings.</Trans>}
      />
    );
  return null;
}

// ─── Overview tab ─────────────────────────────────────────────────────────

function OverviewTab({ member, canRemove }: { member: FamilyMember; canRemove: boolean }) {
  const { t, i18n } = useLingui();
  const queryClient = useQueryClient();
  const [confirmRemove, setConfirmRemove] = useState(false);
  const age = getAge(member.dateOfBirth, t);
  const upcoming = (member.appointments ?? []).filter((a) => a.status !== "cancelled");

  const removeMutation = useMutation({
    mutationFn: () => apiFetch.delete(`/api/account/family/${encodeURIComponent(member.id)}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: FAMILY_KEY });
      showToast(t`Member removed.`);
    },
    onError: () => showToast(t`Could not remove member.`, "error"),
  });

  return (
    <div className="space-y-4">
      {/* Quick stats */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard icon={Calendar} label={t`Upcoming visits`} value={upcoming.length} delay={0} />
        <StatCard
          icon={Pill}
          label={t`Active medications`}
          value={member._count?.prescriptions ?? 0}
          delay={0.05}
        />
        <StatCard
          icon={Syringe}
          label={t`Vaccinations due`}
          value={member._count?.vaccinationRecords ?? 0}
          delay={0.1}
        />
        <StatCard
          icon={Ruler}
          label={t`Measurements`}
          value={member._count?.growthRecords ?? 0}
          delay={0.15}
        />
      </div>

      {/* Upcoming list */}
      {upcoming.length > 0 && (
        <div className="border-border bg-card rounded-xl border p-4">
          <h3 className="text-foreground text-sm font-semibold">
            <Trans>Next appointments</Trans>
          </h3>
          <div className="mt-3 space-y-2">
            {upcoming.map((a) => (
              <div key={a.id} className="bg-muted/50 flex items-center rounded-lg px-3 py-2.5">
                <div className="flex items-center gap-3">
                  <Calendar className="text-primary size-4" aria-hidden />
                  <div>
                    <p className="text-foreground text-sm font-medium">
                      {providerFullDisplayName(a.provider, i18n.locale)}
                    </p>
                    <p className="text-muted-foreground text-xs">
                      {new Date(a.startsAt).toLocaleDateString(i18n.locale, {
                        day: "numeric",
                        month: "short",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Member info */}
      <div className="border-border bg-card rounded-xl border p-4">
        <div className="flex items-start justify-between gap-3">
          <h3 className="text-foreground text-sm font-semibold">
            <Trans>Member info</Trans>
          </h3>
          {canRemove && (
            <button
              type="button"
              onClick={() => setConfirmRemove(true)}
              aria-label={t`Remove member`}
              disabled={removeMutation.isPending}
              className="text-muted-foreground hover:text-destructive -mt-3 -mr-3 flex size-11 items-center justify-center rounded-full transition-colors disabled:opacity-40"
            >
              <Trash2 className="size-4" />
            </button>
          )}
        </div>
        <div className="mt-3 grid grid-cols-2 gap-3">
          <Field label={t`Name`} value={`${member.firstName} ${member.lastName}`} />
          {age && <Field label={t`Age`} value={age} />}
          {member.gender && (
            <Field
              label={t`Gender`}
              value={member.gender === "M" ? t`Male` : member.gender === "F" ? t`Female` : t`Other`}
            />
          )}
          <Field label={t`Role`} value={roleLabel(member.familyRole, t)} />
        </div>
      </div>

      {/* Destructive confirm — ResponsiveSheet per CLAUDE.md, replaces the
          legacy native confirm(). Renders via portal, so the space-y wrapper
          is unaffected. */}
      <ResponsiveSheet
        open={confirmRemove}
        onClose={() => setConfirmRemove(false)}
        title={<Trans>Remove member</Trans>}
        description={t`Remove ${member.firstName} ${member.lastName} from your family group?`}
        width="sm"
        ariaLabel={t`Remove member`}
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="touch" onClick={() => setConfirmRemove(false)}>
              <Trans>Cancel</Trans>
            </Button>
            <Button
              variant="destructive"
              size="touch"
              disabled={removeMutation.isPending}
              onClick={() => {
                setConfirmRemove(false);
                removeMutation.mutate();
              }}
              className="gap-1.5"
            >
              <Trash2 className="size-4" />
              <Trans>Remove</Trans>
            </Button>
          </div>
        }
      >
        {/* Confirmation copy lives in the header description — no body. */}
        <div aria-hidden className="h-1" />
      </ResponsiveSheet>
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-muted-foreground text-xs">{label}</p>
      <p className="text-foreground text-sm font-medium">{value}</p>
    </div>
  );
}

function StatCard({
  icon: Icon,
  label,
  value,
  delay,
}: {
  icon: LucideIcon;
  label: string;
  value: number;
  delay: number;
}) {
  return (
    <m.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay }}
      className="border-border bg-card rounded-xl border p-4"
    >
      <Icon className="text-primary size-5" />
      <p className="text-foreground mt-2 text-2xl font-bold">{value}</p>
      <p className="text-muted-foreground text-xs">{label}</p>
    </m.div>
  );
}

// ─── Plan tab ─────────────────────────────────────────────────────────────

function PlanTab({
  planCode,
  planName,
  memberCount,
  maxMembers,
  limitReached,
  onAddMember,
  onInvite,
  isOwner,
}: {
  planCode: string;
  planName: string;
  memberCount: number;
  maxMembers: number | null;
  limitReached: boolean;
  onAddMember: () => void;
  onInvite: () => void;
  isOwner: boolean;
}) {
  const isFree = planCode === "user_free";
  const unlimited = maxMembers === null;

  // `soon` = not built yet: listed for transparency, never sold as included.
  const benefits: {
    icon: LucideIcon;
    title: React.ReactNode;
    desc: React.ReactNode;
    soon?: boolean;
  }[] = [
    {
      icon: Calendar,
      title: <Trans>Appointments for everyone</Trans>,
      desc: (
        <Trans>
          Book your child with a pediatrician, your spouse with a cardiologist — all from one
          account.
        </Trans>
      ),
    },
    {
      icon: Pill,
      title: <Trans>Medication tracking</Trans>,
      soon: true,
      desc: (
        <Trans>Know exactly what each member takes. Refill alerts and adherence streaks.</Trans>
      ),
    },
    {
      icon: Ruler,
      title: <Trans>Health passport</Trans>,
      soon: true,
      desc: <Trans>WHO growth curves, vaccine calendar, developmental milestones.</Trans>,
    },
    {
      icon: Baby,
      title: <Trans>Pregnancy tracker</Trans>,
      soon: true,
      desc: <Trans>Week-by-week: how the baby grows, prenatal labs, movements.</Trans>,
    },
    {
      icon: Syringe,
      title: <Trans>Vaccination calendar</Trans>,
      soon: true,
      desc: <Trans>Completed and scheduled vaccines, reminders for the next dose.</Trans>,
    },
    {
      icon: Shield,
      title: <Trans>Preventive care</Trans>,
      soon: true,
      desc: <Trans>Age-aware screenings: mammograms, annual labs, and more.</Trans>,
    },
  ];

  return (
    <div className="space-y-5">
      {/* Plan summary card */}
      <div className="border-border bg-card relative overflow-hidden rounded-2xl border p-5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-muted-foreground flex items-center gap-2 text-xs font-medium">
              <Sparkles className="size-3.5" />
              <Trans>Your plan</Trans>
            </div>
            <h3 className="text-foreground mt-1 text-lg font-bold">
              {isFree ? <Trans>Free</Trans> : planName}
            </h3>
            <p className="text-muted-foreground mt-1 text-sm">
              {unlimited ? (
                <Trans>Unlimited family members.</Trans>
              ) : (
                <Trans>
                  {memberCount} of {maxMembers} family members used
                </Trans>
              )}
            </p>
          </div>
          <Badge variant={isFree ? "secondary" : "default"} className="shrink-0">
            {isFree ? <Trans>Free</Trans> : <Trans>Active</Trans>}
          </Badge>
        </div>

        {/* Progress bar */}
        {!unlimited && typeof maxMembers === "number" && maxMembers > 0 && (
          <div className="bg-muted mt-4 h-2 overflow-hidden rounded-full">
            <div
              className={cn(
                "h-full rounded-full transition-all",
                limitReached ? "bg-destructive" : "bg-primary",
              )}
              style={{ width: `${Math.min(100, (memberCount / maxMembers) * 100)}%` }}
            />
          </div>
        )}

        {/* Action row */}
        <div className="mt-5 flex flex-wrap gap-2">
          {isOwner && !limitReached && (
            <>
              <Button size="touch" onClick={onAddMember}>
                <UserPlus className="size-4" />
                <Trans>Add member</Trans>
              </Button>
              <Button size="touch" variant="outline" onClick={onInvite}>
                <Mail className="size-4" />
                <Trans>Invite</Trans>
              </Button>
            </>
          )}
          {isFree && (
            <Link
              to="/account/user/billing"
              search={{ tab: "subscription" } as never}
              className="bg-primary text-primary-foreground hover:bg-primary-hover inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-medium"
            >
              <Crown className="size-4" />
              <Trans>Upgrade to Plus</Trans>
            </Link>
          )}
        </div>

        {limitReached && (
          <div className="border-border bg-muted/40 mt-4 flex items-start gap-2 rounded-lg border p-3 text-sm">
            <Crown className="text-primary mt-0.5 size-4 shrink-0" />
            <p className="text-muted-foreground">
              <Trans>You&apos;ve reached the family member limit on your current plan.</Trans>
            </p>
          </div>
        )}
      </div>

      {/* Benefits grid */}
      <div>
        <div className="mb-3 flex items-center gap-2">
          <LayoutGrid className="text-muted-foreground size-4" />
          <h3 className="text-foreground text-sm font-semibold">
            <Trans>What you can manage</Trans>
          </h3>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {benefits.map((b, i) => {
            const Icon = b.icon;
            return (
              <m.div
                key={i}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05 * i }}
                className="border-border bg-muted/30 flex items-start gap-3 rounded-xl border p-4 text-left"
              >
                <span className={cn("mt-0.5", b.soon ? "text-muted-foreground" : "text-primary")}>
                  <Icon className="size-5" aria-hidden />
                </span>
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="text-foreground text-sm font-semibold">{b.title}</p>
                    {b.soon && (
                      <Badge variant="neutral">
                        <Trans>Coming soon</Trans>
                      </Badge>
                    )}
                  </div>
                  <p className="text-muted-foreground mt-0.5 text-xs">{b.desc}</p>
                </div>
              </m.div>
            );
          })}
        </div>
      </div>

      {/* Plan rules */}
      <div className="border-border bg-card rounded-xl border p-4">
        <h3 className="text-foreground text-sm font-semibold">
          <Trans>How family works</Trans>
        </h3>
        <ul className="text-muted-foreground mt-3 space-y-2 text-xs">
          <li className="flex items-start gap-2">
            <CheckCircle2 className="text-primary mt-0.5 size-4 shrink-0" />
            <Trans>
              <span className="text-foreground font-medium">Add member</span> creates a managed
              profile (no separate login). Use it for children or relatives you care for.
            </Trans>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle2 className="text-primary mt-0.5 size-4 shrink-0" />
            <Trans>
              <span className="text-foreground font-medium">Invite</span> connects an existing
              PontiMed account by email or phone. They confirm via email or SMS.
            </Trans>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle2 className="text-primary mt-0.5 size-4 shrink-0" />
            <Trans>Owners can remove members. Members keep their own data after removal.</Trans>
          </li>
        </ul>
      </div>
    </div>
  );
}

// ─── Pending invites strip ────────────────────────────────────────────────

function PendingInvitesPanel({ invites }: { invites: PendingInvite[] }) {
  return (
    <section>
      <h2 className="text-foreground text-sm font-semibold">
        <Trans>Pending invites</Trans>
      </h2>
      <ul className="mt-3 space-y-2">
        {invites.map((invite) => (
          <InviteRow key={invite.id} invite={invite} />
        ))}
      </ul>
    </section>
  );
}

function InviteRow({ invite }: { invite: PendingInvite }) {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const cancel = useMutation({
    mutationFn: () =>
      apiFetch.delete(`/api/account/family/invite?id=${encodeURIComponent(invite.id)}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: FAMILY_KEY }),
    onError: () => showToast(t`Could not cancel invite.`, "error"),
  });

  const target = invite.targetEmail ?? invite.targetPhone ?? "";

  return (
    <li className="border-border bg-card flex items-center gap-3 rounded-lg border p-3">
      <div className="bg-muted flex size-9 shrink-0 items-center justify-center rounded-full">
        {invite.targetEmail ? <Mail className="size-4" /> : <Phone className="size-4" />}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-foreground truncate text-sm font-medium">{target}</p>
        <p className="text-muted-foreground flex items-center gap-1.5 text-xs">
          <Clock className="size-3" />
          <Trans>Pending</Trans>
          <span aria-hidden>·</span>
          {roleLabel(invite.role, t)}
        </p>
      </div>
      <button
        type="button"
        onClick={() => cancel.mutate()}
        disabled={cancel.isPending}
        aria-label={t`Cancel invite`}
        className="text-muted-foreground hover:text-destructive -my-1.5 flex size-11 shrink-0 items-center justify-center rounded-full transition-colors disabled:opacity-40"
      >
        <X className="size-4" />
      </button>
    </li>
  );
}

// ─── Invite sheet ─────────────────────────────────────────────────────────

function InviteSheet({
  open,
  onClose,
  onInvited,
}: {
  open: boolean;
  onClose: () => void;
  onInvited: () => void;
}) {
  const { t } = useLingui();
  const [target, setTarget] = useState("");
  const [role, setRole] = useState<Relationship | "">("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const reset = useCallback(() => {
    setTarget("");
    setRole("");
    setError("");
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!target || !role) return;
    setError("");
    setSubmitting(true);
    const toast = showToast(t`Sending invite…`, "loading");
    try {
      const isEmail = target.includes("@");
      await apiFetch.post("/api/account/family/invite", {
        [isEmail ? "email" : "phone"]: target,
        role,
      });
      toast.update(t`Invite sent`, "success");
      reset();
      onInvited();
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) {
        const msg = t`That person doesn't have a PontiMed account yet.`;
        setError(msg);
        toast.update(msg, "error");
      } else if (e instanceof ApiError) {
        const payload = e.body as { error?: string; upgradeRequired?: boolean } | null;
        const msg = payload?.upgradeRequired
          ? t`Upgrade your plan to invite more members.`
          : payload?.error || t`Could not send invite.`;
        setError(msg);
        toast.update(msg, "error");
      } else {
        toast.update(t`Network error. Please try again.`, "error");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const title = (
    <span className="flex items-center gap-2">
      <span className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-teal-50 dark:bg-teal-950">
        <Mail className="size-5 text-teal-600 dark:text-teal-400" />
      </span>
      <Trans>Invite to family</Trans>
    </span>
  );

  return (
    <ResponsiveSheet
      open={open}
      onClose={() => {
        reset();
        onClose();
      }}
      title={title}
      description={<Trans>Connect an existing PontiMed account by email or phone.</Trans>}
      width="md"
      ariaLabel={t`Invite to family`}
    >
      <form className="space-y-5" onSubmit={handleSubmit}>
        <div>
          <label className="text-muted-foreground mb-1 block text-xs font-medium">
            <Trans>Email or phone</Trans>
          </label>
          <Input
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            placeholder={t`name@example.com or +40…`}
            className="min-h-[44px]"
            required
          />
        </div>

        <div>
          <label className="text-muted-foreground mb-2 block text-xs font-medium">
            <Trans>Relationship</Trans>
          </label>
          <RelationshipPicker value={role} onChange={setRole} />
        </div>

        <p className="text-muted-foreground text-xs">
          <Trans>The recipient will confirm via email or SMS within 7 days.</Trans>
        </p>

        {error && <p className="text-destructive text-center text-xs">{error}</p>}

        <Button
          type="submit"
          size="lg"
          className="w-full gap-2"
          disabled={submitting || !target || !role}
        >
          {submitting ? (
            <Trans>Sending…</Trans>
          ) : (
            <>
              <Mail className="size-4" />
              <Trans>Send invite</Trans>
            </>
          )}
        </Button>
      </form>
    </ResponsiveSheet>
  );
}

function RelationshipPicker({
  value,
  onChange,
}: {
  value: Relationship | "";
  onChange: (v: Relationship) => void;
}) {
  const { t } = useLingui();
  const options: { id: Relationship; label: string; icon: LucideIcon; color: string }[] = [
    { id: "child", label: t`Child`, icon: Baby, color: "text-amber-500" },
    { id: "spouse", label: t`Spouse`, icon: Heart, color: "text-rose-500" },
    { id: "parent", label: t`Parent`, icon: Users, color: "text-indigo-500" },
    { id: "other", label: t`Other`, icon: UserPlus, color: "text-teal-500" },
  ];
  return (
    <div className="grid grid-cols-4 gap-2">
      {options.map((opt) => {
        const active = value === opt.id;
        const Icon = opt.icon;
        return (
          <button
            key={opt.id}
            type="button"
            onClick={() => onChange(opt.id)}
            className={cn(
              "flex min-h-[44px] flex-col items-center gap-1.5 rounded-xl border-2 p-3 transition-colors",
              active ? "border-primary bg-primary/5" : "border-border hover:border-primary/40",
            )}
            aria-pressed={active}
          >
            <span className={opt.color}>
              <Icon className="size-5" />
            </span>
            <span className="text-foreground text-[0.6875rem] font-medium">{opt.label}</span>
          </button>
        );
      })}
    </div>
  );
}

// ─── Placeholders ─────────────────────────────────────────────────────────

function PlaceholderTab({
  icon,
  title,
  desc,
}: {
  icon: React.ReactNode;
  title: React.ReactNode;
  desc: React.ReactNode;
}) {
  return (
    // Not built yet — the badge keeps it from reading as an empty record.
    <div className="border-border bg-card rounded-xl border p-8 text-center">
      <div className="bg-muted mx-auto flex size-16 items-center justify-center rounded-full">
        <span className="text-muted-foreground" aria-hidden>
          {icon}
        </span>
      </div>
      <Badge variant="neutral" className="mt-4">
        <Trans>Coming soon</Trans>
      </Badge>
      <h3 className="text-foreground mt-2 text-sm font-semibold">{title}</h3>
      <p className="text-muted-foreground mx-auto mt-1 max-w-sm text-xs">{desc}</p>
    </div>
  );
}

// ─── Skeleton + error ─────────────────────────────────────────────────────

function FamilySkeleton() {
  return (
    <div className="space-y-6">
      <div>
        <Skeleton className="h-8 w-32" />
        <Skeleton className="mt-2 h-4 w-64" />
      </div>
      <div className="flex gap-2">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-11 w-28 rounded-full" />
        ))}
      </div>
      <Skeleton className="h-12 w-full rounded-lg" />
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-24 rounded-xl" />
        ))}
      </div>
    </div>
  );
}

function ErrorState({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="border-destructive/40 bg-destructive/5 rounded-xl border p-6 text-center">
      <p className="text-destructive text-sm font-medium">
        <Trans>Could not load family.</Trans>
      </p>
      <Button size="touch" variant="outline" className="mt-4" onClick={onRetry}>
        <Trans>Retry</Trans>
      </Button>
    </div>
  );
}
